import { composeOptionTicker } from '@/features/chart/lib/utils/option-util'
import type { Exchange } from '@/types/generated/common/types'
import type { SurfaceCurrency } from '@/types/generated/public_api'

const leadingTokens = (book: string) => book.toUpperCase().split(/[-_]/)

/**
 * Resolve the analytics book name for `base`/`quote` from the server's surface
 * book list. Returns `undefined` when the exchange has no matching book (the
 * overlay has no data).
 */
export function resolveAnalyticsBook(
  books: readonly SurfaceCurrency[],
  exchange: Exchange,
  base: string,
  quote: string | undefined
): string | undefined {
  const composed = composeOptionTicker(base, quote)
  const names = books.filter((b) => b.exchange === exchange).map((b) => b.currency)
  if (names.includes(composed)) return composed

  const upperBase = base.toUpperCase()
  const sameBase = names.filter((n) => leadingTokens(n)[0] === upperBase)
  if (sameBase.length === 1) return sameBase[0]
  if (sameBase.length > 1 && quote) {
    const upperQuote = quote.toUpperCase()
    return sameBase.find((n) => leadingTokens(n)[1] === upperQuote)
  }
  return undefined
}

/**
 * Resolve a composed book key (`BTC`, `BTC_USDC`, `BTC-USDT`) for `exchange`. Callers that
 * carry one string per tab (data widget, home cards) meet the exchange here, so the same
 * key resolves differently on each venue.
 */
export function resolveAnalyticsBookByName(
  books: readonly SurfaceCurrency[],
  exchange: Exchange,
  name: string
): string | undefined {
  const [base = '', quote] = leadingTokens(name)
  return resolveAnalyticsBook(books, exchange, base, quote)
}

/** The server's book names for one exchange, de-duplicated. */
export function listExchangeBooks(books: readonly SurfaceCurrency[], exchange: Exchange): string[] {
  return [...new Set(books.filter((b) => b.exchange === exchange).map((b) => b.currency))]
}
